/**
 * 流水导出 —— **序列化的唯一实现**（**C 域 · C3 · §5.1.11**）
 *
 * 依据：C 域 PRD **§5.1.11**（导出范围 / 格式 / 跨币种 / 费用）· `AC-C1.35`（CSV 含**交易段 + 腿段**；
 * JSON 含拆分与标签）· **`AC-C1.36`**（🔴 **不含折算列** · 永久免费）· `AC-C1.37`（「恢复预览」**不在**本域）。
 *
 * ## 🔴 为什么序列化必须在共享层、且只能有一份
 *
 * C 域 PRD §5.1.11 写明：「**导出能力的实现唯一在本域**；🔴 **主权入口在 N 域「数据主权区」**
 * —— 同一函数、两个入口，**禁两套实现**」。
 * ⇒ 本文件是那个"同一函数"。服务端的 `GET /api/transactions/export` 与将来 N 域的
 * 「导出我的数据」入口**都调它**；N 域只负责"排除表清单明示"与"单测断言防回归"。
 *
 * ⚠️ **为什么它落在 `contract/` 而不是 `nucleus/`**：它消费的 `TransactionRowView` 是**契约类型**，
 * 而架构规则 `nucleus-no-outward` 把 `nucleus/**` 的依赖白名单限定为
 * `nucleus | types | enums | constants` —— `contract/` **不在其中**。
 * 序列化"某一版对外形状"的产物，本质上就是契约层的事（`contract/` 可以依赖 `nucleus/`，反之不行）。
 *
 * ## 🔴 三条格式纪律
 *
 * 1. **不含任何折算列**（`AC-C1.36` 红线）：金额一律「**原币整数分** + 币种列」，
 *    另给一列**人类可读的元文本**（`amountYuan`）—— 两者都是**原币**，不是本币。
 * 2. **CSV 分两段**（交易段 + 腿段），各带自己的表头，段间以标题行分隔；
 *    退款与原交易的关联由 `parentId` 列保留（`AC-C1.35`）。
 * 3. **JSON 直接给结构化两数组**（`transactions` / `legs`），便于程序消费。
 *
 * ## ⚠️ 本文件**不取时钟**
 *
 * `exportedAt` 之类的元信息由**调用方**填进 `meta`（内核是纯函数，不读系统时间 —— 与本项目
 * 「派生值与时钟都从调用方传入」的一贯纪律一致）。
 */

import type { TransactionRowView } from './transaction.js';
import { centsToYuanText } from '../nucleus/money.js';

// ───────────────────────────── 输出结构 ─────────────────────────────

/** 交易段的一行（**扁平**，不含腿 —— 腿在另一段里，用 `id` 关联） */
export interface ExportTxnLine {
  id: string;
  date: string;
  note: string | null;
  /** 记录人 id 与展示名（`AC-C1.35`「JSON 含成员」） */
  userId: string;
  userName: string;
  status: string;
  excludeFromStats: boolean;
  /** 派生父行形态（`single` / `accountNet` / `transfer`） */
  parentKind: string;
  parentAccountId: string | null;
  parentCurrency: string;
  /** 🔴 原币整数分；转账形态为 `null`（转账取净额无意义 —— `DR-016` §3.7） */
  parentNetCents: number | null;
  parentNetYuan: string | null;
  parentDirection: string;
  transferFromAccountId: string | null;
  transferToAccountId: string | null;
  /** 父子关系（`DR-035` v1.1 后只剩**退款**：退款行 → 被冲减的原交易） */
  parentId: string | null;
  /** 父交易是否已被软删（`AC-C2.15`：「原交易已删除」的标注依据） */
  parentDeleted: boolean | null;
  source: string;
  hasRefundLeg: boolean;
  hasUncategorizedLeg: boolean;
  tagNames: readonly string[];
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
}

/** 腿段的一行 */
export interface ExportLegLine {
  transactionId: string;
  legId: string;
  accountId: string;
  accountName: string;
  /** 🔴 腿币种 = 账户币种（腿不挂币种列 —— 架构 §4.1） */
  currency: string;
  direction: string;
  /** 原币整数分 */
  amountCents: number;
  /** 原币元文本（人类可读；**不是折算值**） */
  amountYuan: string;
  categoryId: string | null;
  categoryName: string | null;
  categoryKind: string | null;
  note: string | null;
  excludeFromStats: boolean;
}

export interface ExportPayload {
  transactions: readonly ExportTxnLine[];
  legs: readonly ExportLegLine[];
}

/** 调用方可选的元信息（服务端填账本/币种/导出时刻 —— **内核不取时钟**） */
export interface ExportMeta {
  ledgerId?: string;
  baseCurrency?: string;
  exportedAt?: number;
  /** 当前筛选条件的人类可读摘要（端侧填；核心不解释筛选语义） */
  filterSummary?: string;
}

// ─────────────────────────── 装配（唯一实现） ───────────────────────────

/** 把行视图摊成「交易段 + 腿段」两数组（**导出与 N 域主权入口共用**） */
export function buildExportPayload(rows: readonly TransactionRowView[]): ExportPayload {
  const transactions: ExportTxnLine[] = [];
  const legs: ExportLegLine[] = [];

  for (const row of rows) {
    const parent = row.parent;
    transactions.push({
      id: row.id,
      date: row.date,
      note: row.note,
      userId: row.userId,
      userName: row.userName,
      status: row.status,
      excludeFromStats: row.excludeFromStats,
      parentKind: parent.kind,
      parentAccountId: parent.mainAccountId,
      parentCurrency: parent.currency,
      parentNetCents: parent.net === null ? null : (parent.net as number),
      parentNetYuan: parent.net === null ? null : centsToYuanText(parent.net),
      parentDirection: parent.direction,
      transferFromAccountId: parent.transfer?.fromAccountId ?? null,
      transferToAccountId: parent.transfer?.toAccountId ?? null,
      parentId: row.parentId,
      parentDeleted: row.parentDeleted,
      source: row.source,
      hasRefundLeg: row.hasRefundLeg,
      hasUncategorizedLeg: row.hasUncategorizedLeg,
      tagNames: row.tagNames,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
      deletedAt: row.deletedAt,
    });

    for (const leg of row.legs) {
      legs.push({
        transactionId: row.id,
        legId: leg.id,
        accountId: leg.accountId,
        accountName: leg.accountName,
        currency: leg.currency,
        direction: leg.direction,
        amountCents: leg.amount,
        amountYuan: centsToYuanText(leg.amount as never),
        categoryId: leg.categoryId,
        categoryName: leg.categoryName,
        categoryKind: leg.categoryKind,
        note: leg.note,
        excludeFromStats: leg.excludeFromStats,
      });
    }
  }

  return { transactions, legs };
}

// ─────────────────────────── CSV ───────────────────────────

/** CSV 字段转义（含逗号 / 引号 / 换行时必须加引号，内部引号翻倍） */
function csvCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = Array.isArray(v) ? v.join('|') : String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvLine(cells: readonly unknown[]): string {
  return cells.map(csvCell).join(',');
}

const TXN_COLUMNS: readonly (keyof ExportTxnLine)[] = [
  'id',
  'date',
  'note',
  'userId',
  'userName',
  'status',
  'excludeFromStats',
  'parentKind',
  'parentAccountId',
  'parentCurrency',
  'parentNetCents',
  'parentNetYuan',
  'parentDirection',
  'transferFromAccountId',
  'transferToAccountId',
  'parentId',
  'parentDeleted',
  'source',
  'hasRefundLeg',
  'hasUncategorizedLeg',
  'tagNames',
  'createdAt',
  'updatedAt',
  'deletedAt',
];

const LEG_COLUMNS: readonly (keyof ExportLegLine)[] = [
  'transactionId',
  'legId',
  'accountId',
  'accountName',
  'currency',
  'direction',
  'amountCents',
  'amountYuan',
  'categoryId',
  'categoryName',
  'categoryKind',
  'note',
  'excludeFromStats',
];

/**
 * 生成 CSV 文本（**两段**：交易段 + 腿段）。
 *
 * 🔴 **首字符是 UTF-8 BOM**（`\uFEFF`）：Excel 打开无 BOM 的 UTF-8 CSV 会把中文显示成乱码，
 * 而"导出的表看不明白"会直接摧毁用户对数据主权的信任（N 域的核心承诺）。
 * BOM 对程序解析无害（多数库会剥掉），故一律加上。
 */
export function toCsv(payload: ExportPayload, meta: ExportMeta = {}): string {
  const head: string[] = [];
  head.push(`# 家盈流水导出 · 交易段（${payload.transactions.length} 行）`);
  if (meta.baseCurrency !== undefined) head.push(`# 账本本币：${meta.baseCurrency}`);
  if (meta.filterSummary !== undefined && meta.filterSummary !== '') head.push(`# 筛选：${meta.filterSummary}`);
  head.push('# 🔴 金额为**原币整数分**（amountCents / parentNetCents），全部**未折算**');

  const txnLines = [
    csvLine(['id', 'date', 'note', 'userId', 'userName', 'status', 'excludeFromStats', 'parentKind', 'parentAccountId', 'parentCurrency', 'parentNetCents', 'parentNetYuan', 'parentDirection', 'transferFromAccountId', 'transferToAccountId', 'parentId', 'parentDeleted', 'source', 'hasRefundLeg', 'hasUncategorizedLeg', 'tagNames', 'createdAt', 'updatedAt', 'deletedAt']),
    ...payload.transactions.map((t) => csvLine(TXN_COLUMNS.map((c) => t[c]))),
  ];

  const legLines = [
    `# 腿段（${payload.legs.length} 行）—— 用 transactionId 关联交易段`,
    csvLine(['transactionId', 'legId', 'accountId', 'accountName', 'currency', 'direction', 'amountCents', 'amountYuan', 'categoryId', 'categoryName', 'categoryKind', 'note', 'excludeFromStats']),
    ...payload.legs.map((l) => csvLine(LEG_COLUMNS.map((c) => l[c]))),
  ];

  return `\uFEFF${[...head, '', ...txnLines, '', ...legLines].join('\n')}\n`;
}

/**
 * 生成 JSON 文本。
 *
 * 🔴 **两个数组 + 元信息**，不做嵌套（`AC-C1.35` 要的是"含退款关联与标签"，不是树）——
 * 嵌套会让"某条腿属于哪笔"在解析侧再算一次，而扁平结构里 `parentId` 一眼可见。
 */
export function toJson(payload: ExportPayload, meta: ExportMeta = {}): string {
  return `${JSON.stringify(
    {
      format: 'jiaying.transactions.v1',
      ledgerId: meta.ledgerId ?? null,
      baseCurrency: meta.baseCurrency ?? null,
      exportedAt: meta.exportedAt ?? null,
      filter: meta.filterSummary ?? null,
      counts: { transactions: payload.transactions.length, legs: payload.legs.length },
      transactions: payload.transactions,
      legs: payload.legs,
    },
    null,
    2,
  )}\n`;
}

/** 导出文件名（端侧也可用；**唯一来源**，避免两端各拼一次日期串） */
export function exportFileName(format: 'csv' | 'json', day: string, ledgerId?: string): string {
  const suffix = ledgerId === undefined ? '' : `-${ledgerId}`;
  return `jiaying-transactions${suffix}-${day}.${format}`;
}
